import { useMemo } from "react"

import {
  useCustomerBusinessDetailsRetrieve,
  useCustomerCompanyList,
  useCustomerPrequalifyingBusinessDetailsRetrieve,
  useCustomerPrequalifyingFundingNeedsRetrieve,
} from "@boundless/bl-api-client/customer/services"
import { useStore } from "zustand"

import { isNotFoundError } from "@/lib/clients"
import { companyListQueryOptions } from "@/lib/companies"
import { store } from "@/lib/store"
import { isEmbed } from "@/lib/utils"

const isRealError = (error: unknown) => !isNotFoundError(error)

/** Nothing to resume from: no company of ours, no answers saved. */
const NO_ANSWERS = {
  resumeCompany: null,
  resumeBusiness: null,
  resumeFunding: null,
} as const

/**
 * Whether the onboarding wizard is owed, derived from data rather than a
 * dismissal flag — completing it is what stops it reappearing.
 *
 * Excluded: borrowers who already have real Business Details (they answered
 * the long way round), borrowers with more than one company (the wizard
 * belongs to signing up, not to the Add Company dialog), and the embed, where
 * `EmbedLink.fulfill()` already captured these answers from the partner.
 */
export function useOnboardingWizard() {
  const userRole = useStore(store, (s) => s.userRole)
  const activeCompanyUuid = useStore(store, (s) => s.activeCompanyUuid)
  const companyUuid = activeCompanyUuid ?? ""
  const isCustomer = userRole === "customer"
  // `EmbedLink.fulfill()` already captured these answers from the partner, so
  // the embed is out. Read once and used twice: it gates the queries, so a
  // partner's borrower never fires the four requests we would only throw
  // away, and it gates the decision below, which is what actually keeps the
  // wizard shut.
  const inEmbed = isEmbed()
  const enabled = isCustomer && !!companyUuid && !inEmbed

  // Every observer of this list must share these options, or the next
  // invalidation drops `activeCompanyUuid`. See `companyListQueryOptions`.
  // Nothing below can read `companies` without it, and a failed list is
  // indistinguishable from an account with no company at all.
  const { data: companies, isPending: companiesPending } =
    useCustomerCompanyList(
      {},
      {
        query: {
          ...companyListQueryOptions(isCustomer).query,
          throwOnError: true,
        },
      },
    )

  // A 404 here means "not answered yet". Anything else is a real error.
  const { data: businessDetails, isPending: detailsPending } =
    useCustomerBusinessDetailsRetrieve(companyUuid, {
      query: { enabled, retry: false, throwOnError: isRealError },
    })
  const { data: prequalBusiness, isPending: prequalBusinessPending } =
    useCustomerPrequalifyingBusinessDetailsRetrieve(companyUuid, {
      query: { enabled, retry: false, throwOnError: isRealError },
    })
  const { data: prequalFunding, isPending: prequalFundingPending } =
    useCustomerPrequalifyingFundingNeedsRetrieve(companyUuid, {
      query: { enabled, retry: false, throwOnError: isRealError },
    })

  const hasNoCompany = !companiesPending && companies?.length === 0

  // The list has to catch up with the active company first. With none picked
  // the per-company queries are disabled and read as "never answered"; just
  // after Add Company creates one the list is stale, so a second business
  // would look like a first.
  const isResolvingActiveCompany =
    !companiesPending &&
    !!companies?.length &&
    (!activeCompanyUuid ||
      !companies.some((company) => company.uuid === activeCompanyUuid))

  const isLoading =
    companiesPending ||
    isResolvingActiveCompany ||
    (enabled &&
      (detailsPending || prequalBusinessPending || prequalFundingPending))

  // Memoised: every branch below builds a fresh object, so without this the
  // hook never hands back the same reference twice and no caller can put the
  // result in a dependency array.
  return useMemo(() => {
    const closed = {
      shouldOpen: false,
      isLoading: false,
      ...NO_ANSWERS,
    }

    if (!isCustomer || inEmbed) {
      return closed
    }

    if (isLoading) {
      return { shouldOpen: false, isLoading: true, ...NO_ANSWERS }
    }

    // Someone with no company at all has answered nothing, so they get the
    // wizard from the top.
    if (hasNoCompany) {
      return { shouldOpen: true, isLoading: false, ...NO_ANSWERS }
    }

    // Been here before. Not `>= 1`: the wizard creates a company on its own
    // first question, so that would strand anyone refreshing mid-wizard.
    if ((companies?.length ?? 0) > 1) {
      return closed
    }

    if (businessDetails) {
      return closed
    }

    return {
      shouldOpen: !prequalBusiness || !prequalFunding,
      isLoading: false,
      // An unanswered borrower's single company is one the wizard created and
      // abandoned. Reuse it, or the name step makes another on every pass.
      resumeCompany: companies?.[0] ?? null,
      // What an earlier run already saved, so a borrower who reloads finds the
      // wizard where they left it with their answers still in the fields.
      resumeBusiness: prequalBusiness ?? null,
      resumeFunding: prequalFunding ?? null,
    }
  }, [
    isCustomer,
    inEmbed,
    isLoading,
    hasNoCompany,
    companies,
    businessDetails,
    prequalBusiness,
    prequalFunding,
  ])
}
